import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { DetailInput, DynamicPlayerBadge, DatePicker, ClickOutside } from "@myplay/ui";
import { utils } from "@myplay/all";
import dayjs from 'dayjs';

import I18N from '../../I18N';
import { ImagePickerModal } from '..';
import { capitalize } from '../../utils/stringManipulations';
import './Settings.scss';

const Settings = ({ UserStore, isVisible, closeMenu }) => {
  const {
    data: { firstName, lastName, phone, birthdate, country, avatar, avatarBgColor },
    allCountries,
    updateUserDetails,
    logout, 
    updateUserImage
  } = UserStore;
  const [updatedFirstName, setFirstName] = useState(null);
  const [updatedLastName, setLastName] = useState(null);
  const [updatedPhone, setPhone] = useState(null);
  const [updatedCountry, setCountry] = useState(null);
  const [updatedBirthdate, setBirthdate] = useState(null);
  const [shouldDisplaySaveChanges, setSaveChangeFlag] = useState(false);
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    phone: ""
  });
  const [shouldDisplayDatePicker, setDatePickerFlag] = useState(false);
  const [ newImage, setNewImage ] = useState(null);

  useEffect(() => {
    if (!isVisible) {
      setFirstName(null);
      setBirthdate(null);
      setLastName(null);
      setPhone(null);
      setCountry(null);
      setErrors({
        firstName: "",
        lastName: "",
        phone: ""
      })
    }
  }, [isVisible])

  useEffect(() => {
    if (
      updatedFirstName && updatedFirstName !== firstName ||
      updatedLastName && updatedLastName !== lastName ||
      updatedPhone && updatedPhone !== phone ||
      updatedCountry && updatedCountry !== country ||
      updatedBirthdate && dayjs(updatedBirthdate).format("MMM DD,YYYY") !== dayjs(birthdate).format("MMM DD,YYYY")
    ) {
      setSaveChangeFlag(true)
    } else {
      setSaveChangeFlag(false)
    }
  }, [updatedFirstName, updatedLastName, updatedPhone, updatedCountry, updatedBirthdate])


  const handleNameChange = name => {
    setFirstName(name);
    setErrors(e => {
      return { ...e, firstName: name.length < 2 ? capitalize(I18N.t("SHORT_NAME_ERROR")) : "" }
    });
  };

  const handleLastNameChange = name => {
    setLastName(name);
    setErrors(e => {
      return { ...e, lastName: name.length < 2 ? capitalize(I18N.t("SHORT_NAME_ERROR")) : "" }
    });
  };

  const handlePhoneChange = phone => {
    setPhone(phone);
    setErrors(e => {
      return { ...e, phone: phone && !utils.isValidPhone(phone) ? capitalize(I18N.t("INVALID_PHONE")) : "" }
    });
  };

  const handleCountryChange = country => {
    setCountry(country)
  };

  const handleDatePick = date => {
    setBirthdate(date.toISOString());
    setDatePickerFlag(false);
  };

  const handleSaveChanges = () => {
    const details = {}
    if (errors.firsName || errors.lastName || errors.phone) {
      return;
    };

    if (updatedFirstName && updatedFirstName !== firstName) {
      details.firstName = updatedFirstName;
    };

    if (updatedBirthdate && updatedBirthdate !== birthdate) {
      details.birthdate = updatedBirthdate;
    };

    if (updatedLastName && updatedLastName !== lastName) {
      details.lastName = updatedLastName;
    };

    if (updatedPhone && updatedPhone !== phone) {
      details.phone = updatedPhone;
    };

    if (updatedCountry && country !== updatedCountry) {
      details.country = updatedCountry;
    };

    updateUserDetails(details);
    closeMenu();
  };

  const handleNewImage = event => {
    const reader = new FileReader();
    reader.onloadend = onLoadEvent => {
      setNewImage(onLoadEvent.target.result);
    };

    utils.ImageTools.resize(
      event.target.files[0],
      {
        width: 540,
        height: 360
      },
      compressed => {
        reader.readAsDataURL(compressed);
      }
    );
  };


  const discardNewImage = ()=> {
    setNewImage(null);
  };

  const handleSaveNewImage = async img => {
    await updateUserImage(img);
    setNewImage(null);
  }

  return (
    <ClickOutside onClickOutside={closeMenu}>
      <div 
        className="settings" 
        style={{ maxHeight: isVisible ? "400px" : "0px", minHeight: shouldDisplayDatePicker ? "450px" : "0px" }}
        data-testid="settings"
      >
        <div className="settings__image-inputs-container">
          <div className="settings__image-inputs-container__image">
            <DynamicPlayerBadge
              avatar={avatar}
              firstName={firstName}
              lastName={lastName}
              avatarBgColor={avatarBgColor}
              badgeSize={90}
            />
            <div className="settings__image-inputs-container__image__input">
              <input
                type="file"
                id="new-image"
                style={{ display: "none" }}
                value=""

                onChange={event => handleNewImage(event)}
              />
              {/* eslint-disable */}
              <label htmlFor="new-image" id="new-file">
                {I18N.t("CHANGE").toUpperCase()}
              </label>
              {/* eslint-enable */}
            </div>
          </div>
          <div className="settings__image-inputs-container__inputs">
            <DetailInput
              value={updatedFirstName === null ? firstName : updatedFirstName}
              label={I18N.t("NAME").toUpperCase()}
              onChange={handleNameChange}
              error={errors.firstName}
            />
            <DetailInput
              value={updatedLastName === null ? lastName : updatedLastName}
              label={I18N.t("LAST_NAME").toUpperCase()}
              onChange={handleLastNameChange}
              error={errors.lastName}
            />
            <DetailInput
              value={dayjs(updatedBirthdate === null ? birthdate : updatedBirthdate).format("MMM DD,YYYY")}
              label={I18N.t("DATE_OF_BIRTH").toUpperCase()}
              onClick={() => setDatePickerFlag(true)}
            />
            {shouldDisplayDatePicker &&
              <ClickOutside onClickOutside={() => setDatePickerFlag(false)}>
                <div className="settings__image-inputs-container__inputs__date-picker-wrapper">
                  <DatePicker
                    onChange={handleDatePick}
                  />
                </div>
              </ClickOutside>
            }
            {country && 
              <DetailInput
                label={I18N.t("COUNTRY").toUpperCase()}
                type="select"
                defaultSelectedItem={{ value: country }}
                items={allCountries}
                onChange={handleCountryChange}
              />
            }
            <DetailInput
              value={updatedPhone === null ? phone : updatedPhone}
              label={I18N.t("PHONE").toUpperCase()}
              onChange={handlePhoneChange}
              error={errors.phone}
            />
            {shouldDisplaySaveChanges &&
              <button 
                className="settings__image-inputs-container__inputs__save" 
                onClick={handleSaveChanges}
                data-testid="save-changes"
              >
                {capitalize(I18N.t("SAVE_CHANGES"))}
              </button>
            }
          </div>
        </div>
        <div className="settings__footer">
          <button onClick={logout} data-testid="logout">
            {capitalize(I18N.t("LOGOUT"))}
          </button>
        </div>
      </div>
      <ImagePickerModal img={newImage} onCancel={discardNewImage} onSave={handleSaveNewImage}/>
    </ClickOutside>
  );
};

Settings.propTypes = {
  closeMenu: PropTypes.func, 
  isVisible: PropTypes.bool, 
  UserStore: PropTypes.object
};

Settings.defaultProps = {
  closeMenu: ()=> {}, 
  isVisible: false, 
  UserStore: {}
};

export default inject("UserStore")(observer(Settings));